import React from 'react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { Settings, Search, Moon, Sun } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useActiveVertical } from '../hooks/useActiveVertical';
import { useSearchStore } from '../stores/searchStore';
import NotificationBell, { getBellAudience } from './NotificationBell';
import { fieldSearchGroups, recordAudience } from '../lib/record-paths';
import AgentMark from './AgentMark';
import { useAiAgentProfile } from '../hooks/useAiAgentProfile';
import { isSalesRole } from '../lib/sales-access';
import fieldiqIcon from '../assets/fieldiq-icon.png';
import fieldiqIconLight from '../assets/fieldiq-icon-light.png';
import fieldiqWordmark from '../assets/fieldiq-wordmark.png';
import fieldiqWordmarkLight from '../assets/fieldiq-wordmark-light.png';
import { Tooltip } from './ui/tooltip';

/**
 * `bellPanelClassName` is passed through to NotificationBell's `panelClassName`
 * (position and width only). Casing and the office pass nothing and keep the
 * bell's default. The trucking / rentals field shell passes its phone-safe
 * placement (fe#815) — see components/field/FieldPageChrome.
 */
export default function Header({ bellPanelClassName }: { bellPanelClassName?: string } = {}) {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';
  const { company, isFieldWorker, user, hasVertical } = useAuth();
  const navigate = useNavigate();
  const activeVertical = useActiveVertical();
  const openSearch = useSearchStore((s) => s.open);
  const { data: agentProfile } = useAiAgentProfile({ enabled: !isSalesRole(user?.role) });
  const agentAvatarUrl = agentProfile?.avatar_url || '';
  // See DesktopSidebar: avatar_url is not always a URL, so a broken image falls back to the
  // AgentMark rather than leaving the button empty.
  const [agentAvatarFailed, setAgentAvatarFailed] = React.useState(false);
  const showAgentAvatar = Boolean(agentAvatarUrl) && !agentAvatarFailed;
  const icon = theme === 'light' ? fieldiqIconLight : fieldiqIcon;
  const wordmark = theme === 'light' ? fieldiqWordmarkLight : fieldiqWordmark;
  const companyName: string | null = company?.name ?? null;
  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/i.test(navigator.platform || navigator.userAgent || '');
  const shortcutHint = isMac ? '⌘K' : 'Ctrl K';
  // A field hand is offered Search only when their tree can open something it
  // finds (lib/record-paths.ts): casing jobs and equipment, or a driver's BOLs.
  // A rental tech, or a downhole or PT hand, has no such page, so no button.
  const showSearch = !isFieldWorker || fieldSearchGroups(recordAudience(true, hasVertical), hasVertical).length > 0;

  return (
    <header
      className="bg-background border-b border-fieldiq-border print:hidden"
      style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
    >
      {/* PHONE WIDTH: THE COMPANY NAME TRUNCATES, IT NEVER OVERLAPS. At 390px the
        * control cluster on the right is ~270px of fixed-size buttons, and the brand
        * button used to be an ordinary flex item whose min-width is its content, so a
        * long name wrapped to three lines and slid under the search icon. Below md
        * the brand takes the space that is left (flex-1, min-w-0) and the name
        * truncates, and the padding, gaps and icon come down so there is room for
        * more than an ellipsis. Every md: value below puts desktop back exactly as it
        * was. Pinned by Header.mobile-truncate.test.tsx. */}
      <div className="px-4 md:px-6 py-5 flex justify-between items-center gap-3 md:gap-0">
        <Tooltip content={companyName ?? undefined} className="min-w-0 flex-1 md:flex-initial md:min-w-[auto]">
          <button
            onClick={() => navigate('/')}
            className="min-w-0 flex-1 md:flex-initial md:min-w-[auto] hover:opacity-80 transition-opacity text-left"
          >
            {/* Mobile: icon + company name + vertical name */}
            <div className="flex min-w-0 items-center gap-2 md:hidden">
              <img src={icon} alt="FieldIQ" className="h-10 w-10 shrink-0 rounded-xl object-cover" />
              <div data-testid="header-brand-text" className="flex min-w-0 flex-col">
                {companyName && (
                  <p data-testid="header-company-name" className="truncate text-sm font-bold text-foreground leading-tight">{companyName}</p>
                )}
                {activeVertical && (
                  <p className="truncate text-xs font-medium text-fieldiq-muted leading-tight">{activeVertical.display_name}</p>
                )}
              </div>
            </div>
            {/* Desktop: wordmark + company name beside it + vertical name below */}
            <div className="hidden md:flex md:items-center md:gap-4">
              <img src={wordmark} alt="FieldIQ" className="h-14 object-contain" />
              {(companyName || activeVertical) && (
                <div className="flex flex-col border-l border-fieldiq-border pl-4">
                  {companyName && (
                    <p className="text-base font-bold text-foreground leading-tight">{companyName}</p>
                  )}
                  {activeVertical && (
                    /* THE VERTICAL LABEL IS THE TOKEN, NOT A SLATE STEP, AND THE
                       REASON IS A MEASUREMENT. It shipped in a fixed pale-steel
                       step of the cool-grey scale (#cbd5e1), which theme.css does
                       not remap — none of that scale is in the light-mode
                       override blocks — so the one value had to serve both
                       backgrounds and could not. THE CLASS IS DESCRIBED AND NOT
                       NAMED HERE ON PURPOSE: Tailwind's scanner reads a comment
                       as source, and this was its last call site, so spelling it
                       out would keep the retired rule in the bundle with nothing
                       using it (CLAUDE.md, instances 3 and 5). On
                       the dark header (#12182B) it reads 12.2:1; on the light one
                       (--background, #EFF1F4) it reads **1.31:1**, which is not
                       dim, it is gone. It is drawn under the company name on
                       every page in the app, and nothing reported it: the class
                       is in the DOM and the build is clean.
                       `--fieldiq-muted` follows the theme — #919395 dark, #5B6471
                       light — and is what the rest of the app spells a secondary
                       line with: 5.86:1 dark, 5.29:1 light. Same fix on the
                       mobile line above. */
                    <p className="text-sm font-medium text-fieldiq-muted leading-tight mt-1">{activeVertical.display_name}</p>
                  )}
                </div>
              )}
            </div>
          </button>
        </Tooltip>

        <div data-testid="header-controls" className="flex shrink-0 items-center gap-1.5 md:gap-4">
          {showSearch && (
            <>
            <button
              onClick={openSearch}
              aria-label="Open search"
              className="hidden md:flex items-center gap-2 h-10 w-64 px-3 bg-background border border-fieldiq-border rounded-lg text-sm text-muted-foreground hover:border-fieldiq-accent/60 transition-colors"
            >
              <Search size={16} />
              <span className="flex-1 text-left">Search…</span>
              <kbd className="px-1.5 py-0.5 text-[10px] font-medium rounded border border-fieldiq-border bg-fieldiq-card text-muted-foreground">
                {shortcutHint}
              </kbd>
            </button>
            <button
              onClick={openSearch}
              aria-label="Open search"
              className="md:hidden p-3 hover:bg-white/10 rounded-xl transition"
            >
              <Search size={22} className="text-fieldiq-accent" />
            </button>
            </>
          )}
          {/* FieldIQ Agent, mobile only. There is no sidebar on a phone, so the footer
            * entry that replaced the floating button has nowhere to live — the agent joins
            * the existing control cluster instead, with the same chrome as theme and
            * settings beside it rather than a shape of its own. Desktop reaches the agent
            * from the sidebar footer, so the breakpoint utility below drops it there.
            * Drawn for sales too since api#1076 opened the assistant to them; the
            * profile stays closed, so a sales user gets the AgentMark. */}
          <button
            onClick={() => navigate('/agent')}
            aria-label="FieldIQ Agent"
            className="md:hidden p-2.5 bg-secondary border border-border rounded-lg hover:bg-accent transition-colors overflow-hidden"
          >
            {showAgentAvatar ? (
              <img
                src={agentAvatarUrl}
                alt=""
                className="rounded-full object-cover"
                style={{ width: 22, height: 22 }}
                onError={() => setAgentAvatarFailed(true)}
              />
            ) : (
              <AgentMark />
            )}
          </button>
          <NotificationBell panelClassName={bellPanelClassName} />
          {/* THEME: DESKTOP ONLY. On a phone the five controls left the company name
            * about 55px (#807), so the toggle moved into the menu the gear opens —
            * SettingsIndexPage for the office, FieldMoreScreen for a field hand —
            * and md: puts it back here exactly as it was. */}
          <button
            onClick={toggleTheme}
            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            className="hidden md:block p-2.5 bg-secondary border border-border rounded-lg hover:bg-accent transition-colors"
          >
            {isDark ? (
              <Sun size={22} className="text-fieldiq-accent" />
            ) : (
              <Moon size={22} className="text-fieldiq-accent" />
            )}
          </button>
          {/* A field worker's route tree has no /settings: it fell through to the
            * catch-all and landed on /field, so the gear did nothing. Their menu,
            * which now carries the theme row, is /field/more. */}
          {/* A trucking driver or rental tech is in the /my-work tree, which has
            * no /field/more (its rows are casing-tree routes): there the gear
            * opens their Profile, which holds notification settings and sign
            * out. Same audience split as the bell (getBellAudience). */}
          {/* Settings is closed to sales (lib/sales-access.ts), so no gear. */}
          {!isSalesRole(user?.role) && (
          <button
            onClick={() => navigate(!isFieldWorker ? '/settings' : getBellAudience(isFieldWorker, hasVertical) === 'my-work' ? '/my-work/profile' : '/field/more')}
            aria-label="Settings"
            className="p-2.5 bg-secondary border border-border rounded-lg hover:bg-accent transition-colors"
          >
            <Settings size={22} className="text-fieldiq-accent" />
          </button>
          )}
        </div>
      </div>
    </header>
  );
}
